import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { accent6 as accent, useSlide6, Seg } from "./slide6.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 6 — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide6.data.ts. */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.65, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[]) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: "#c8f0f0", fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide6Mobile: React.FC = () => {
  const { meta: slide6Meta, stages: slide6Stages, callout: slide6Callout } = useSlide6();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#4a9494", textTransform: "uppercase", marginBottom: 8 }}>
        {slide6Meta.breadcrumbPre} · <span style={{ color: accent }}>{slide6Meta.breadcrumbAccent}</span>
      </div>

      {/* title */}
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 27, lineHeight: 1.18, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide6Meta.titlePlain} <span style={{ color: accent }}>{slide6Meta.titleAccent}</span>
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#6a9a9a", margin: "0 0 20px", lineHeight: 1.5 }}>
        {slide6Meta.subtitle}
      </p>

      {/* four stages */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide6Meta.stagesLabel}</div>
        {slide6Stages.map((s, i) => (
          <div key={i} style={{
            display: "flex", gap: 13, alignItems: "flex-start",
            background: s.rem ? "rgba(56,181,181,0.08)" : "rgba(255,255,255,0.02)",
            border: `1px solid ${s.rem ? "rgba(56,181,181,0.3)" : "rgba(56,181,181,0.1)"}`,
            borderRadius: 9, padding: "11px 13px", marginBottom: 8,
          }}>
            <div style={{ flexShrink: 0, width: 62 }}>
              <div style={{ fontSize: 11, fontFamily: mono, color: s.rem ? accent : "rgba(56,181,181,0.6)", letterSpacing: 0.5 }}>{s.stage}</div>
              <div style={{ fontSize: 9, fontFamily: mono, color: "#4a6880", marginTop: 3 }}>{s.duration}</div>
            </div>
            <div>
              <div style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 15, color: s.rem ? "#a0e4e4" : "#c8dff0", marginBottom: 3 }}>{s.type}</div>
              <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 13.5, lineHeight: 1.5, color: s.rem ? "#88d8d8" : "#9bbdd6", margin: 0 }}>{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* callout */}
      <div style={{ background: "rgba(56,181,181,0.06)", border: "1px solid rgba(56,181,181,0.20)", borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{slide6Meta.calloutLabel}</div>
        <p style={{ ...txt, color: "#9adcdc" }}>{renderSegs(slide6Callout)}</p>
      </div>
    </div>
  );
};

export default HTLDSlide6Mobile;
